/** iCalendar (RFC 5545) text for invitations and subscribed feeds (U85). */

export type Partstat = 'NEEDS-ACTION' | 'ACCEPTED' | 'DECLINED' | 'TENTATIVE';

export interface IcsEvent {
  uid: string;
  sequence?: number;
  start: Date;
  end: Date;
  summary: string;
  description?: string;
  location?: string;
  url?: string;
  cancelled?: boolean;
  organizer?: { name: string; email: string };
  attendees?: { name: string; email: string; partstat: Partstat }[];
  /** Last change (DTSTAMP); defaults to `stamp` of the calendar. */
  stamp?: Date;
}

export interface IcsCalendar {
  name?: string;
  method?: 'PUBLISH' | 'REQUEST' | 'CANCEL';
  stamp: Date;
  events: readonly IcsEvent[];
}

/** UTC date-time in basic format, e.g. 20261012T120000Z. */
export function icsDate(d: Date): string {
  return d
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

/** TEXT value escaping (backslash, semicolon, comma, newline). */
export function icsText(s: string): string {
  return s.replace(/[\\;,]/g, (c) => `\\${c}`).replace(/\r?\n/g, '\\n');
}

/** Parameter value (names in CN=…): quoted, without characters a quoted value cannot hold. */
const param = (s: string) => `"${s.replace(/["\r\n]/g, '')}"`;

/** UTF-8 length of one code point. */
const octets = (ch: string) => {
  const c = ch.codePointAt(0)!;
  return c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
};

/** Folds a content line at 75 octets without splitting UTF-8 characters. */
export function foldLine(line: string): string {
  const out: string[] = [];
  let current = '';
  let size = 0;
  for (const ch of line) {
    const n = octets(ch);
    const limit = out.length === 0 ? 75 : 74; // continuation lines start with a space
    if (size + n > limit) {
      out.push(current);
      current = '';
      size = 0;
    }
    current += ch;
    size += n;
  }
  out.push(current);
  return out.join('\r\n ');
}

function eventLines(e: IcsEvent, stamp: Date): string[] {
  const lines = [
    'BEGIN:VEVENT',
    `UID:${e.uid}`,
    `SEQUENCE:${e.sequence ?? 0}`,
    `DTSTAMP:${icsDate(e.stamp ?? stamp)}`,
    `DTSTART:${icsDate(e.start)}`,
    `DTEND:${icsDate(e.end)}`,
    `SUMMARY:${icsText(e.summary)}`,
  ];
  if (e.description) lines.push(`DESCRIPTION:${icsText(e.description)}`);
  if (e.location) lines.push(`LOCATION:${icsText(e.location)}`);
  if (e.url) lines.push(`URL:${e.url}`);
  lines.push(`STATUS:${e.cancelled ? 'CANCELLED' : 'CONFIRMED'}`);
  if (e.organizer)
    lines.push(`ORGANIZER;CN=${param(e.organizer.name)}:mailto:${e.organizer.email}`);
  for (const a of e.attendees ?? [])
    lines.push(
      `ATTENDEE;CN=${param(a.name)};ROLE=REQ-PARTICIPANT;PARTSTAT=${a.partstat};RSVP=FALSE:mailto:${a.email}`,
    );
  lines.push('END:VEVENT');
  return lines;
}

/** A whole VCALENDAR with CRLF line ends. */
export function icsCalendar(cal: IcsCalendar): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//stageplottter//U85//EN',
    'CALSCALE:GREGORIAN',
  ];
  if (cal.method) lines.push(`METHOD:${cal.method}`);
  if (cal.name) lines.push(`X-WR-CALNAME:${icsText(cal.name)}`);
  for (const e of cal.events) lines.push(...eventLines(e, cal.stamp));
  lines.push('END:VCALENDAR');
  return lines.map(foldLine).join('\r\n') + '\r\n';
}
